import { useId, useState } from "react";

import {
  defaultQuestionBlocks,
  QUESTION_BLOCK_LABELS,
  QUESTION_BLOCKS,
  type QuestionBlock,
  resolveQuestionBlocks,
} from "@upclaim/lib/src/passenger-answers";

import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Textarea } from "@/components/ui/textarea";

import { type ClaimWithRelations, useClaimPassengerQuestionnaire } from "../../api/use-claim";
import { useSaveCustomerNote } from "../../api/use-claim-mutations";

function readBlocks(value: ClaimWithRelations["passengerQuestionBlocks"]): string[] | null {
  return Array.isArray(value) ? value.filter((block): block is string => typeof block === "string") : null;
}

/**
 * What the passengers are asked at CONFIRM (TECH-1097): question blocks to tick, switched on by
 * default as the claim's travel agency sets them (TECH-1352), and the free question as a last resort.
 * Upclaim only (its callers gate it): agency users see the answers, not the questions.
 */
export function PassengerQuestionsEditor({ claim }: { claim: ClaimWithRelations }) {
  const { data: settings } = useClaimPassengerQuestionnaire(claim.id);
  const agencyBlocks = settings?.agencyBlocks ?? null;
  const saveQuestionnaire = useSaveCustomerNote(claim.id, "Questionnaire mis à jour");
  const saveFreeQuestion = useSaveCustomerNote(claim.id, "Question aux passagers enregistrée");
  const idPrefix = useId();
  const claimTypeName = claim.claimType?.name ?? null;
  const configured = readBlocks(claim.passengerQuestionBlocks);

  const [blocks, setBlocks] = useState<QuestionBlock[] | null>(
    configured ? resolveQuestionBlocks(claimTypeName, configured, agencyBlocks) : null,
  );
  const [passengersQuestion, setPassengersQuestion] = useState(claim.passengersQuestion ?? "");

  const defaults = defaultQuestionBlocks(claimTypeName, agencyBlocks);
  const effective = blocks ?? defaults;

  /** Saved on each tick, like a switch: nothing waits for « Sauvegarder », which is the free question's. */
  function saveBlocks(next: QuestionBlock[]) {
    setBlocks(next);
    saveQuestionnaire.mutate({ passengerQuestionBlocks: next });
  }

  function toggle(block: QuestionBlock, checked: boolean) {
    const next = checked ? [...effective, block] : effective.filter((item) => item !== block);
    saveBlocks(QUESTION_BLOCKS.filter((item) => next.includes(item)));
  }

  function onSave() {
    // Only the free question: the blocks save themselves, the customer note from its own field.
    saveFreeQuestion.mutate({ passengersQuestion });
  }

  return (
    <div className="flex w-full flex-col gap-5">
      {/* Waits for the agency's defaults: ticking before would save the global ones. */}
      {settings ? (
        <fieldset className="w-full">
          <legend className="mb-2 text-sm text-gray-600">
            Questions posées au passager à la confirmation
          </legend>
          <ul className="divide-y rounded-lg border">
            {QUESTION_BLOCKS.map((block) => {
              const id = `${idPrefix}-block-${block}`;
              return (
                <li key={block}>
                  <label
                    htmlFor={id}
                    className="flex cursor-pointer items-start gap-3 px-3 py-2.5 text-sm hover:bg-gray-50"
                  >
                    <Checkbox
                      id={id}
                      className="mt-0.5"
                      checked={effective.includes(block)}
                      disabled={saveQuestionnaire.isPending}
                      onCheckedChange={(next) => toggle(block, next === true)}
                    />
                    <span className="min-w-0 flex-1">
                      <span className="font-medium text-gray-900">{QUESTION_BLOCK_LABELS[block].label}</span>
                      {!blocks && defaults.includes(block) ? (
                        <span className="ml-2 text-xs text-gray-400">par défaut</span>
                      ) : null}
                      <span className="block text-xs text-gray-500">
                        {QUESTION_BLOCK_LABELS[block].description}
                      </span>
                    </span>
                  </label>
                </li>
              );
            })}
          </ul>
        </fieldset>
      ) : null}

      <div className="w-full">
        <label htmlFor={`${idPrefix}-question`} className="mb-2 block text-sm text-gray-600">
          Question libre (en dernier recours)
        </label>
        <Textarea
          rows={2}
          name="passengers-question"
          id={`${idPrefix}-question`}
          placeholder="Affichée en fin de questionnaire, avec un champ de réponse facultatif"
          className="block w-full"
          value={passengersQuestion}
          onChange={(event) => setPassengersQuestion(event.target.value)}
        />
      </div>

      <div className="flex justify-end">
        <Button variant="outline" size="sm" onClick={onSave} disabled={saveFreeQuestion.isPending}>
          Sauvegarder
        </Button>
      </div>
    </div>
  );
}
